import type { PropertyCalendar } from '@/api/client'
import { isCleaningDrawn } from '@/components/calendar/cleaning-bars'
import { buildDepositMarkers } from '@/lib/deposit-events'
import { gapTouchesMonth, snapshotCaveats } from '@/lib/gap-events'
import { buildReviewMarkers, reviewToday } from '@/lib/review-events'
import { buildSupplyMarkers } from '@/lib/supply-events'

/**
 * The layers the property calendar can show, in toggle order.
 *
 * Adding a layer is: a row here, its bars or markers in the tab, and its body
 * in the sheet. The API names each layer the same way (`layers.<id>`), so a
 * layer the UI does not know yet is simply never listed - it is not drawn and
 * it does not get an empty toggle.
 */
export const CALENDAR_LAYERS = [
  { id: 'stays', label: 'Stays' },
  { id: 'cleaning', label: 'Cleanings' },
  { id: 'blocks', label: 'Blocks' },
  { id: 'deposits', label: 'Deposits' },
  { id: 'supplies', label: 'Supplies' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'orphan_gaps', label: 'Gaps' },
] as const

export type CalendarLayerId = (typeof CALENDAR_LAYERS)[number]['id']

export type LayerSummary =
  | {
      status: 'ok'
      count: number
      /**
       * Why this layer's answer is not a clean one (a stale or cut-short gap
       * snapshot): plain sentences, shown on the chip and in the toolbar so a
       * dated answer is never taken for a current one.
       */
      caveats?: string[]
    }
  | { status: 'error'; message: string }
  /** No data yet (still loading, or the whole read failed). */
  | { status: 'pending' }

/**
 * `month` (`YYYY-MM`) is the month on screen. The deposits layer returns each
 * stay's whole timeline, and the supplies layer lists a category for a runout
 * past the range, so their counts have to be the markers that fall in this
 * month - what switching the layer on puts on the grid - not the items read.
 * The reviews layer lists a stay for any month it touches and a marker is one
 * of its three days, so it counts markers too.
 */
export function summarizeLayer(
  calendar: PropertyCalendar | undefined,
  id: CalendarLayerId,
  month: string
): LayerSummary {
  if (!calendar) return { status: 'pending' }
  const layer = calendar.layers[id]
  if (layer.status !== 'ok') return { status: 'error', message: layer.message }
  if (id === 'orphan_gaps' && calendar.layers.orphan_gaps.status === 'ok') {
    // A gap is returned whole and listed for every month it touches, so the
    // count is the gaps with a night in this month.
    const gaps = calendar.layers.orphan_gaps
    const caveats = snapshotCaveats(gaps, calendar.time_zone)
    return {
      status: 'ok',
      count: gaps.items.filter(gap => gapTouchesMonth(gap, month)).length,
      ...(caveats.length > 0 ? { caveats } : {}),
    }
  }
  // The count is what switching the layer on shows: a cancelled cleaning is
  // in the read but has no bar.
  let count: number
  if (id === 'cleaning' && calendar.layers.cleaning.status === 'ok') {
    count = calendar.layers.cleaning.items.filter(isCleaningDrawn).length
  } else if (id === 'deposits' && calendar.layers.deposits.status === 'ok') {
    count = buildDepositMarkers(
      calendar.layers.deposits.items,
      calendar.time_zone,
      day => day.slice(0, 7) === month
    ).length
  } else if (id === 'supplies' && calendar.layers.supplies.status === 'ok') {
    count = buildSupplyMarkers(
      calendar.layers.supplies.items,
      day => day.slice(0, 7) === month
    ).length
  } else if (id === 'reviews' && calendar.layers.reviews.status === 'ok') {
    count = buildReviewMarkers(calendar.layers.reviews.items, {
      today: reviewToday(calendar.generated_at, calendar.time_zone),
      inDay: day => day.slice(0, 7) === month,
    }).length
  } else {
    count = layer.items.length
  }
  return { status: 'ok', count }
}
